import React, { useEffect, useState } from 'react';
import styles from './MyProgress.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { getMyProgress } from '../../core/store/myProgress/myProgress';
import { getUserInfo } from '../../shared/constants/utilities';
import { Skeleton } from '@mui/material';
import globals from '../../shared/constants/global.constant';
import { VictoryAxis, VictoryChart, VictoryLine, VictoryScatter } from 'victory';
import moment from 'moment';
import { useMediaQuery } from 'react-responsive';

export const MyProgress = () => {
  const muaMobile = useMediaQuery({
    query: `(max-width: 480px)`,
  })
  const dispatch = useDispatch()
  const { common, myProgress } = useSelector((state) => state)
  const { user } = common
  const { myProgressData, isLoading, isSuccess } = myProgress
  const [chartData, setChartData] = useState([])
  const [xAxisLabels, setXAxisLabels] = useState([])
  const [referenceDate, setReferenceDate] = useState(null)

  const { id: userId } = getUserInfo()
  const { Tier, IsTrackingActivity, DaysToUpdateTier } = user

  useEffect(() => {
    dispatch(getMyProgress(userId))
  }, [dispatch, userId])

  const transformProgressData = (data) => {
    const tierValues = {
      'Starter': 1,
      'Riser': 2,
      'Pro': 3,
      'Master': 4
    };
  
    return data.map(item => {
      const date = new Date(item.Start);
      const month = date.toLocaleString('en-US', { month: 'short' });
      const day = date.getDate();
      return {
        x: `${month} ${day}`,
        y: tierValues[item.Tier] || 1
      };
    });
  };
  
  const CustomTickLabel = (props) => {
    const { x, y, datum } = props;
    const tier = datum === 1 ? 'starter' : datum === 2 ? 'riser' : datum === 3 ? 'pro' : 'master';
    
    return (
      <g transform={`translate(${x},${y})`}>
        <image
          href={`${globals.logosBasePath}${tier}Icon.svg`}
          width={muaMobile ? "24px" : "14px"}
          height={muaMobile ? "24px" : "14px"}
          y={muaMobile ? -12 : -8}
          x={muaMobile ? -4 : -8}
        />
      </g>
    );
  };

  useEffect(() => {
    if (isSuccess) {
      if (myProgressData && myProgressData.length > 0) {
        const transformedData = transformProgressData(myProgressData)
        setChartData(transformedData)
        
        const dates = myProgressData.map(item => {
          const date = new Date(item.Start);
          const month = date.toLocaleString('en-US', { month: 'short' });
          const day = date.getDate();
          return `${month} ${day}`;
        });
        setXAxisLabels(dates);
        
        const date = myProgressData[myProgressData.length - 1].Start
        setReferenceDate(date)
      } else {
        const currentDate = new Date()
        setReferenceDate(currentDate)
        setXAxisLabels([])
        setChartData([])
      }
    }
  }, [isSuccess, myProgressData])

  const getTier = (tier) => {
    switch (tier) {
      case 'Starter':
        return 'starter'
      case 'Pro':
        return 'pro'
      case 'Master':
        return 'master'
      case 'Riser':
        return 'riser'
      default:
        return 'starter'
    }
  }

  const getText = (tier) => {
    switch (tier) {
      case 'Starter': 
        return {
          title: "Getting <strong>started</strong>",
          description: "You're at the beginning of your journey. Every small interaction helps you build awareness and start shaping the habits that support long-term growth. Focus on consistency, take time to reflect, and use this space to explore how you respond to the demands of daily work.",
        }
      case 'Pro':
        return {
          title: '<strong>Pro</strong> in motion',
          description: "You're using the platform with clear purpose, showing strong engagement and self-awareness. Keep refining your rhythm, reflect on what's driving progress, and begin tuning in to how emotional patterns influence your performance and decision-making in real time.",
        }
      case 'Master':
        return {
          title: 'Mastering your <strong>growth</strong>',
          description: "You've reached a high level of engagement and reflection. Your habits are strong and your insights are sharper. This is your chance to go deeper. Identify what fuels your performance, reduce interferences, and build readiness into how you lead, decide, and adapt.",
        }
      case 'Riser':
        return {
          title: 'Momentum is <strong>building</strong>',
          description: "You're engaging more regularly and starting to build reliable patterns. This is where momentum begins to form. Focus on keeping your pace steady, taking moments to reflect on what's working, and experimenting with new behaviors that improve your readiness at work.",
        }
      default:
        return {
          title: "Getting <strong>started</strong>",
          description: "You're at the beginning of your journey. Every small interaction helps you build awareness and start shaping the habits that support long-term growth. Focus on consistency, take time to reflect, and use this space to explore how you respond to the demands of daily work.",
        }
    }
  }

  return (
    <section className={styles.myProgress}>
      <div className={styles.container}>
        <div className={styles.wrapperTitle}>
          <div className={styles.title}>
            {`${common?.user?.FirstName} ${common?.user?.LastName}`}
          </div>
        </div>
        <div className={styles.wrapperSubtitle}>
          <p className={styles.subtitle}>My</p>
          &nbsp;
          <p className={styles.subtitle2}>Progress</p>
        </div>
        {isLoading ? (
          <div className={styles.wrapperProgress}>
            <Skeleton 
              variant="rounded" 
              width={'100%'} 
              height={"264px"} 
              sx={{ borderRadius: '12px' }} 
            />
            <Skeleton 
              variant="rounded" 
              width={'100%'} 
              height={"320px"}
              sx={{ borderRadius: '12px' }}
            />
          </div>
        ) : (
          <div className={styles.wrapperProgress}>
            <div className={styles.wrapperProgressTracker}>
              <div className={styles.wrapperProgressHeader}>
                <div className={styles.progressTrackerTitle}>
                  Progress Tracker
                </div>
                <div className={styles.progressTrackerText}>
                  {DaysToUpdateTier} days before the next update
                </div>
              </div>
              <div className={styles.progressTrackerDate}>
                {moment(referenceDate).format('MM/DD/YYYY')}
              </div>
              <div className={styles.wrapperProgressTrackerContent}>
                <div className={styles.progressTrackerInfo}>
                  <div className={styles.progressTrackerInfoTitle}>
                    <span dangerouslySetInnerHTML={{ __html: getText(Tier).title }} />
                  </div>
                  <div className={styles.progressTrackerInfoText}>
                    <span dangerouslySetInnerHTML={{ __html: getText(Tier).description }} />
                  </div>
                </div>
                <div className={styles.trakerImage}>
                  <img
                    src={`${globals.logosBasePath}${getTier(Tier)}.svg`}
                    alt={getTier(Tier)}
                    height={"100%"}
                    style={{
                      opacity: IsTrackingActivity ? 1 : 0.4,
                    }}
                  />
                </div>
              </div>
            </div>
            <div className={styles.wrapperProgressTimeline}>
              <div className={styles.progressTimelineTitle}>
                Progress Timeline
              </div>
              <div className={styles.progressTimelineText}>
                Looking back and moving forward
              </div>
              <div className={styles.chartContainer}>
                <VictoryChart
                  domainPadding={15}
                  height={muaMobile ? 320 : 120}
                  padding={{ 
                    top: 0, 
                    bottom: 20, 
                    left: 10, 
                    right: 30 
                  }}
                  domain={{ y: [0.5, 4.5] }}
                >
                  <VictoryAxis
                    dependentAxis
                    orientation="right"
                    tickValues={[1, 2, 3, 4]}
                    tickLabelComponent={<CustomTickLabel />}
                    style={{
                      axis: { display: 'none' },
                      grid: { stroke: '#1A3127', strokeWidth: 0.2 },
                    }}
                  />
                  <VictoryAxis
                    tickValues={xAxisLabels}
                    style={{
                      axis: { stroke: '#1A3127', strokeWidth: 0.2 },
                      grid: { stroke: 'none' },
                      tickLabels: {
                        fontSize: muaMobile ? 12 : 6,
                        fill: '#1A3127',
                        fontFamily: 'Matter',
                        fontWeight: 'lighter',
                        padding: muaMobile ? 8 : 0
                      },
                    }}
                  />
                  <VictoryLine
                    data={chartData.slice(0, -2).filter(point => point.y !== 0)}
                    style={{
                      data: {
                        stroke: "#4EA7AC",
                        strokeWidth: 1
                      }
                    }}
                  />
                  <VictoryLine
                    data={chartData.slice(-3).filter(point => point.y !== 0)}
                    style={{
                      data: {
                        stroke: "#ececec",
                        strokeWidth: 1,
                        strokeDasharray: "2,2"
                      }
                    }}
                  />
                  <VictoryScatter
                    data={chartData.filter(point => point.y !== 0)}
                    size={4}
                    style={{
                      data: {
                        fill: ({ index }) => {
                          const totalPoints = chartData.filter(point => point.y !== 0).length;
                          return index >= totalPoints - 2 ? "#ececec" : "#4EA7AC";
                        },
                        stroke: ({ index }) => {
                          const totalPoints = chartData.filter(point => point.y !== 0).length;
                          return index >= totalPoints - 2 ? "#4EA7AC" : "none";
                        },
                        strokeWidth: 1
                      }
                    }}
                  />
                </VictoryChart>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  )
};

